import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../shared/axiosInstance";
import { API } from "../../constants";
import {
  IApproveInvoiceRequest,
  IBedrockRejectInvoiceRequest,
  IRegenerateInvoiceRequest,
  IUpdateVendorInvoiceNoRequest,
} from "./invoices.api.types";
import { invoiceKeys } from "./invoices.queries";

// Bedrock errors are { error } or { errors: [] }; the mock used { message }.
export const getBedrockErrorMessage = (err: any): string => {
  const data = err?.response?.data;
  return (
    data?.error ||
    (Array.isArray(data?.errors) ? data.errors.join(", ") : "") ||
    data?.message ||
    err?.message ||
    ""
  );
};

export const updateVendorInvoiceNo = async (
  payload: IUpdateVendorInvoiceNoRequest
) => {
  const res = await axiosInstance.post(
    `${process.env.REACT_APP_BASE_URL}/${API.INVOICE.UPDATE_INVOICE_NUMBER}`,
    payload
  );
  return res.data;
};

export const useUpdateVendorInvoiceNoMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateVendorInvoiceNo,
    onSuccess: (data, payload) => {
      const saved = data?.data?.vendor_invoice_no ?? payload.vendorInvoiceNo;
      queryClient.setQueryData(
        invoiceKeys.vendorInvoiceNo(String(payload.invoiceId)),
        String(saved ?? "")
      );
      return queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
    },
  });
};

export const approveInvoice = async ({
  invoiceId,
  remarks,
}: IApproveInvoiceRequest) => {
  const trimmed = remarks?.trim();
  const res = await axiosInstance.post(
    `${process.env.REACT_APP_BASE_URL}/${API.INVOICE.buildBedrockApproveUrl(invoiceId)}`,
    trimmed ? { remarks: trimmed } : {}
  );
  return res.data;
};

export const useApproveInvoiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: approveInvoice,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
};

export const rejectInvoice = async ({
  invoiceId,
  rejectionReason,
  rejectionComments,
}: IBedrockRejectInvoiceRequest) => {
  const res = await axiosInstance.post(
    `${process.env.REACT_APP_BASE_URL}/${API.INVOICE.buildBedrockRejectUrl(invoiceId)}`,
    { rejectionReason, rejectionComments: rejectionComments.trim() }
  );
  return res.data;
};

export const useRejectInvoiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rejectInvoice,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
};

export const regenerateInvoice = async ({
  invoiceId,
}: IRegenerateInvoiceRequest) => {
  const res = await axiosInstance.post(
    `${process.env.REACT_APP_BASE_URL}/${API.INVOICE.buildBedrockRegenerateUrl(
      invoiceId
    )}`,
    {}
  );
  return res.data;
};

export const useRegenerateInvoiceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateInvoice,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all }),
  });
};
